export interface SoulMapPracticeSummary {
  id: number;
  name: string;
  created_at: string;
  prompt_id?: number;
  completed_at?: string | null;
}

export type SoulMapLandingSessionState =
  | 'new'
  | 'session_1_in_progress'
  | 'session_1_complete'
  | 'session_2_in_progress'
  | 'sessions_complete';

export interface SoulMapSessionProgress {
  state: SoulMapLandingSessionState;
  session1Practice: SoulMapPracticeSummary | null;
  session2Practice: SoulMapPracticeSummary | null;
}

interface SoulMapStepPromptRef {
  id?: number;
  name?: string;
}

function findLatestPractice(
  practices: SoulMapPracticeSummary[] | undefined,
  promptRef: SoulMapStepPromptRef | undefined
): SoulMapPracticeSummary | null {
  if (!practices?.length || !promptRef) {
    return null;
  }

  const promptId = promptRef.id;
  const normalizedAgentName = promptRef.name?.trim().toLowerCase();

  const matches = practices.filter((practice) => {
    if (promptId != null && practice.prompt_id != null) {
      return practice.prompt_id === promptId;
    }

    return (
      Boolean(normalizedAgentName) &&
      practice.name?.trim().toLowerCase() === normalizedAgentName
    );
  });

  if (!matches.length) {
    return null;
  }

  return matches.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )[0];
}

export function resolveSoulMapSessionProgress(
  practices: SoulMapPracticeSummary[] | undefined,
  session1Prompt: SoulMapStepPromptRef | undefined,
  session2Prompt: SoulMapStepPromptRef | undefined
): SoulMapSessionProgress {
  const session1Practice = findLatestPractice(practices, session1Prompt);
  const session2Practice = findLatestPractice(practices, session2Prompt);

  const isDistinctSession2Practice =
    session2Practice != null &&
    (session1Practice == null || session2Practice.id !== session1Practice.id);

  if (
    session1Practice?.completed_at &&
    isDistinctSession2Practice &&
    session2Practice.completed_at
  ) {
    return {
      state: 'sessions_complete',
      session1Practice,
      session2Practice,
    };
  }

  if (isDistinctSession2Practice && !session2Practice.completed_at) {
    return {
      state: 'session_2_in_progress',
      session1Practice,
      session2Practice,
    };
  }

  if (session1Practice?.completed_at) {
    return {
      state: 'session_1_complete',
      session1Practice,
      session2Practice: isDistinctSession2Practice ? session2Practice : null,
    };
  }

  if (session1Practice) {
    return {
      state: 'session_1_in_progress',
      session1Practice,
      session2Practice: null,
    };
  }

  return {
    state: 'new',
    session1Practice: null,
    session2Practice: null,
  };
}
